<template>
  <div class="d-flex space-between">
    <v-autocomplete
      :id="id"
      :class="extraInfo"
      ref="moreAutoComplete"
      v-model="selectedData"
      :items="items"
      :search-input.sync="search"
      :loading="isLoading"
      :multiple="multiple"
      :label="label"
      :error-messages="errorMessages"
      :disabled="disabled"
      :placeholder="placeholder"
      :prepend-inner-icon="prependInnerIcon"
      :clearable="clearable"
      :hide-details="hideDetails"
      :item-text="itemText"
      :item-value="itemValue"
      :small-chips="multiple || chips"
      :deletable-chips="multiple"
      :hint="hint"
      :persistent-hint="persistentHint"
      :chips="chips"
      :solo="solo"
      :flat="flat"
      cache-items
      hide-selected
      hide-no-data
      @focus="populateOnFocus"
      @blur="resetPage"
      @keypress.enter.prevent="searchText"
    >
      <template slot="item" slot-scope="data">
        <v-list-item-content>
          <v-list-item-title>
            {{ data.item[itemText] }}
          </v-list-item-title>
        </v-list-item-content>
      </template>
      <template v-slot:append-item>
        <div v-if="(!fullyLoaded && showMoreIcon) || (showMoreIcon && dirty)">
          <v-list-item-content
            class="px-4 pointer primary--text font-weight-bold"
          >
            <v-list-item-title
              @click="
                loadMore = true;
                fetchData();
              "
            >
              Load More Items ...
            </v-list-item-title>
          </v-list-item-content>
        </div>
      </template>
    </v-autocomplete>
    <slot></slot>
  </div>
</template>
<script>
export default {
  props: {
    value: {
      type: [Number, String, Array, Object],
      default: undefined
    },
    placeholder: {
      type: String,
      default: ""
    },
    extraInfo: {
      type: String,
      default: ""
    },
    id: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default() {
        return [];
      }
    },
    endpoint: {
      type: String,
      default: ""
    },
    itemText: {
      type: String,
      required: true
    },
    itemValue: {
      type: String,
      required: true
    },
    default: {
      type: [Object, Array, String],
      required: false,
      default: null
    },
    hint: {
      type: String,
      default: undefined
    },
    persistentHint: {
      type: Boolean,
      required: false,
      default: false
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    chips: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    prependInnerIcon: {
      type: String,
      default: undefined
    },
    itemsToExclude: {
      type: [Array, Number],
      default: null
    },
    forceFetch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      items: [],
      selectedData: null,
      search: null,
      initialLoadCompleted: false,
      showMoreIcon: false,
      nextLimit: null,
      nextOffset: null,
      loadMore: false,
      dirty: false,
      fullyLoaded: false
    };
  },
  watch: {
    value: {
      handler: function(val) {
        if (val) {
          if (typeof val !== "object") {
            if (this.forceFetch) {
              this.fetchData();
            } else {
              return;
            }
          } else if (!Array.isArray(val)) {
            this.items.push(val);
          } else {
            if (val[0] && typeof val[0] === "object") {
              this.items = val;
            }
          }
          this.updateSelectedData();
          if (this.dirty) {
            this.fetchData();
          }
        } else {
          this.selectedData = "";
        }
      },
      immediate: true
    },
    selectedData(val) {
      this.search = "";
      this.syncSelectedData(val);
      this.$emit("input", val);
    },
    params: {
      handler(oldVal, newVal) {
        if (JSON.stringify(oldVal) === JSON.stringify(newVal)) return;
        this.fetchData();
      },
      deep: true
    }
  },

  methods: {
    async populateOnFocus() {
      if (this.initialLoadCompleted) return;
      await this.populateInitialData();
      this.initialLoadCompleted = true;
    },
    populateInitialData() {
      this.fetchData();
      this.updateSelectedData();
    },
    fetchData() {
      if (this.isLoading || this.fullyLoaded) return;
      this.isLoading = true;
      let nextLimit = this.nextLimit;
      let nextOffset = this.nextOffset;
      if (this.search && !this.loadMore) {
        nextLimit = null;
        nextOffset = null;
      }
      this.$axios
        .get(this.endpoint, {
          params: {
            ...this.params,
            search: this.dirty ? this.search : "",
            limit: nextLimit,
            offset: nextOffset
          }
        })
        .then(response => {
          if (!response.data.results) {
            response.data.results = response.data;
          }
          if (response.data.next) {
            this.showMoreIcon = true;
            this.extractLimitOffset(response.data.next);
          } else {
            this.showMoreIcon = false;
            if (!this.search) {
              this.fullyLoaded = true;
            }
          }
          if (this.itemsToExclude) {
            this.excludeRecord(response.data);
          }
          this.items = response.data.results;
          if (this.search) {
            this.syncSelectedData(this.search);
          }
          if (this.loadMore) {
            this.$nextTick(() => {
              this.$refs.moreAutoComplete.onScroll();
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.loadMore = false;
        });
    },
    updateSelectedData() {
      if (Array.isArray(this.value)) {
        if (typeof this.value[0] === "object") {
          this.selectedData = this.value.map(el => el[this.itemValue]);
        } else {
          this.selectedData = this.value;
        }
      } else if (this.value === null) {
        this.selectedData = "";
      } else if (typeof this.value === "object") {
        this.selectedData = this.value[this.itemValue];
      } else {
        this.selectedData = this.value;
      }
      this.$emit("input", this.selectedData);
    },
    syncSelectedData(val) {
      if (val instanceof Array) {
        const syncData = [];
        val.forEach(key => {
          syncData.unshift(
            this.items.find(data => data[this.itemValue] === key)
          );
        });
        this.$emit("update:selected", syncData);
      } else {
        const syncData = this.items.find(data => data[this.itemValue] === val);
        const selectedFullData = this.items.filter(
          x => x[this.itemValue] === val
        );
        this.$emit("update:selected", syncData);
        this.$emit("update:selectedFullData", selectedFullData[0]);
      }
    },
    // Excludes the selected records from the list if value exists in prop itemsToExclude
    excludeRecord(response) {
      let excludedItem = [];
      if (typeof this.itemsToExclude === "number") {
        excludedItem.push(this.itemsToExclude);
      } else {
        excludedItem = this.itemsToExclude;
      }
      response.results = response.results.filter(item => {
        if (item.id) {
          return !excludedItem.includes(item.id);
        } else if (item.slug) {
          return !excludedItem.includes(item.slug);
        }
      });
    },
    searchText() {
      this.dirty = true;
      this.fetchData();
      this.$emit("change");
    },
    extractLimitOffset(nextLink) {
      const link = new URL(nextLink);
      this.nextLimit = link.searchParams.get("limit");
      this.nextOffset = link.searchParams.get("offset");
    },
    resetPage() {
      this.$emit("blur");
      if (
        this.loadMore ||
        (this.selectedData && !this.multiple) ||
        !this.dirty ||
        this.fullyLoaded
      ) {
        return;
      }
      this.search = "";
      // this.nextLimit = null;
      // this.nextOffset = null;
      this.fetchData();
    }
  }
};
</script>
